import { FC } from 'react'
import { useNavigate } from 'react-router-dom'

import { IndicatorsList, TasksList, Widget } from '@factorialco/f0-react/dist/experimental'
import {
  ContactWidgetTasksInsightDocument,
  ContactWidgetTasksInsightQuery,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { buildWidget } from 'modules/core/lib/widgets'

const prepareData = (data: ContactWidgetTasksInsightQuery) => {
  const tasksList = data.projectManagement.tasks.nodes.map((i) => i.task).filter((i) => !!i) || []
  const showMoreCount = Math.max(
    (data.projectManagement.tasks.totalCount || 0) - tasksList.length,
    0
  )

  const dueCount =
    (data?.projectManagement.dueInfuture.totalCount ?? 0) +
    (data?.projectManagement.dueToday.totalCount ?? 0)
  const overdueCount = data?.projectManagement.overdue.totalCount ?? 0
  const noDueCount = data?.projectManagement.noDueDate.totalCount ?? 0

  const hasTasks = overdueCount > 0 || dueCount > 0 || noDueCount > 0

  return {
    title: i18n.t('insights.widgets.tasks.title'),
    todoTasks: tasksList.filter((task) => task.status === 'todo').map((task) => task.name),
    inProgressTasks: tasksList
      .filter((task) => task.status === 'in_progress')
      .map((task) => task.name),
    overdue: {
      label: i18n.t('insights.widgets.tasks.overdue'),
      content: overdueCount.toString(),
    },
    due: {
      label: i18n.t('insights.widgets.tasks.due'),
      content: dueCount.toString(),
    },
    noDue: {
      label: i18n.t('insights.widgets.tasks.no_due'),
      content: noDueCount.toString(),
    },
    buttonLabel:
      showMoreCount && hasTasks
        ? i18n.tp('insights.widgets.tasks.see_more', { count: showMoreCount })
        : undefined,
  }
}

type PreparedData = ReturnType<typeof prepareData>

const TasksWidgetComponent: FC<{ data: PreparedData }> = ({
  data: { title, todoTasks, inProgressTasks, overdue, due, noDue, buttonLabel },
}) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const linkUrl = navigation.tasks.fullPath()

  const handleNavigate = () => {
    navigate(linkUrl)
  }

  const link = {
    url: linkUrl,
    title: i18n.t('insights.widgets.tasks.title'),
  }

  return (
    <Widget
      header={{
        title,
        link,
      }}
      action={buttonLabel ? { label: buttonLabel, onClick: handleNavigate } : undefined}
    >
      <IndicatorsList
        items={[
          {
            ...overdue,
          },
          {
            ...due,
          },
          {
            ...noDue,
          },
        ]}
      />
      <TasksList
        tasks={{
          inProgress: inProgressTasks,
          todo: todoTasks,
        }}
        emptyMessage={i18n.t('insights.widgets.tasks.empty')}
      />
    </Widget>
  )
}

export const buildTasksWidget = () =>
  buildWidget({
    id: 'tasks',
    document: ContactWidgetTasksInsightDocument,
    prepareData,
    component: TasksWidgetComponent,
  })
